/**
 * RecipeStore - Centralized state management for recipe data
 * 
 * This is the single source of truth for all recipe-related state,
 * eliminating synchronization bugs between different views.
 */

import type { RecipeStore, RecipeStoreState, RecipeEntry, PendingLink, StoreEventMap } from './types.js';
import { EditionMode } from './types.js';
import type { DisplayNode } from '../recipe_renderer/types.js';
import type { Link } from '../recipe_renderer/types.js';
import { assignLinkShape } from '../recipe_renderer/types.js';
import { ChangeTracker, type GraphChanges } from '../recipe_renderer/ChangeTracker.js';
import { resolveNodeTitle, DisplayGraph } from '../recipe_renderer/DisplayGraph.js';
import { NODE_RADIUS } from '../recipe_renderer/dimensions.js';
import { reorderWithCascade, type StepOrderGraph } from '../recipe_renderer/stepOrder.js';
import type { RecipeNode, RecipeListItem, Recipe, RecipeGraph } from '../api/types.js';

/**
 * Implementation of RecipeStore
 */
export class RecipeStoreImpl implements RecipeStore {
  private state: RecipeStoreState;
  private subscribers: Set<() => void> = new Set();
  private eventListeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private changeTracker: ChangeTracker | null = null;
  /** The recipe changeTracker is currently scoped to — nodePool holds every loaded recipe at once, so self-tracking mutators must check a mutation belongs to *this* recipe before touching the tracker. Kept in lockstep with changeTracker by initializeChangeTracker()/resetChangeTracker()/clearChangeTracker(). */
  private trackedRecipeId: string | null = null;

  constructor() {
    this.state = {
      nodePool: new Map(),
      linkPool: [],
      loadedRecipes: new Map(),
      selectedRecipeId: null,
      focusedRecipeId: null,
      selectedNodeIds: new Set(),
      collapsedNodeIds: new Set(),
      hiddenNodeIds: new Set(),
      editMode: false,
      multiSelectMode: false,
      hoveredNodeId: null,
      showHiddenTools: false,

      // Hidden recipes tracking
      hiddenRecipes: new Set(),
      searchHiddenRecipes: new Set(),
      recipesHiddenByFocus: new Set(),

      // Edit mode state
      editionSubMode: EditionMode.NONE,
      pendingLink: null,
    };
  }

  // ============================================================================
  // Getters
  // ============================================================================

  getState(): RecipeStoreState {
    return this.state;
  }

  getNode(nodeId: string): DisplayNode | undefined {
    return this.state.nodePool.get(nodeId);
  }

  getSelectedNode(): DisplayNode | null {
    const firstId = this.state.selectedNodeIds.values().next().value;
    if (!firstId) {
      return null;
    }
    return this.state.nodePool.get(firstId) ?? null;
  }

  /** Every currently selected node (order follows insertion into the set). */
  getSelectedNodes(): DisplayNode[] {
    const nodes: DisplayNode[] = [];
    for (const id of this.state.selectedNodeIds) {
      const node = this.state.nodePool.get(id);
      if (node) nodes.push(node);
    }
    return nodes;
  }

  isMultiSelectMode(): boolean {
    return this.state.multiSelectMode;
  }

  isShowHiddenTools(): boolean {
    return this.state.showHiddenTools;
  }

  getSelectedRecipeId(): string | null {
    return this.state.selectedRecipeId;
  }

  getFocusedRecipeId(): string | null {
    return this.state.focusedRecipeId;
  }

  isNodeExpanded(nodeId: string): boolean {
    return !this.state.collapsedNodeIds.has(nodeId);
  }

  isNodeSelected(nodeId: string): boolean {
    return this.state.selectedNodeIds.has(nodeId);
  }

  isNodeHidden(nodeId: string): boolean {
    return this.state.hiddenNodeIds.has(nodeId);
  }

  isEditMode(): boolean {
    return this.state.editMode;
  }

  getHoveredNodeId(): string | null {
    return this.state.hoveredNodeId;
  }

  isNodeHovered(nodeId: string): boolean {
    return this.state.hoveredNodeId === nodeId;
  }

  // ============================================================================
  // Edit Mode State Getters
  // ============================================================================

  getEditionSubMode(): EditionMode {
    return this.state.editionSubMode;
  }

  getPendingLink(): PendingLink | null {
    return this.state.pendingLink;
  }

  hasUnsavedChanges(): boolean {
    return this.changeTracker?.hasChanges() ?? false;
  }

  getOriginalGraph(): DisplayGraph | null {
    return this.changeTracker?.getOriginalGraph() ?? null;
  }

  // ============================================================================
  // Hidden Recipes Getters
  // ============================================================================

  isRecipeHidden(recipeId: string): boolean {
    return this.state.hiddenRecipes.has(recipeId);
  }

  getHiddenRecipes(): Set<string> {
    return new Set(this.state.hiddenRecipes);
  }

  // ============================================================================
  // Data Loading Helpers
  // ============================================================================

  getLoadedRecipes(): Map<string, RecipeEntry> {
    return new Map(this.state.loadedRecipes);
  }

  getRecipeEntry(recipeId: string): RecipeEntry | undefined {
    return this.state.loadedRecipes.get(recipeId);
  }

  getRecipeNodes(recipeId: string): DisplayNode[] {
    return Array.from(this.state.nodePool.values()).filter(n => n.recipeId === recipeId);
  }

  /** Determine node type from API data */
  private getNodeType(node: RecipeNode): DisplayNode['type'] {
    const hasIngredient = !!node.ingredientInfo;
    const hasTransformation = !!node.transformationInfos?.length;
    if (hasIngredient && hasTransformation) return 'combined';
    if (hasIngredient) return 'ingredient';
    if (hasTransformation) return 'transformation';
    return 'ingredient';
  }

  /** Create a DisplayNode from API RecipeNode data */
  createDisplayNodeFromApi(
    apiNode: RecipeNode,
    recipeId: string,
    isMainRoot: boolean,
  ): DisplayNode {
    return {
      id: apiNode.id,
      name: resolveNodeTitle(apiNode),
      type: this.getNodeType(apiNode),
      data: apiNode,
      recipeId: recipeId,
      isMainRoot: isMainRoot,
      leafDistance: 0,
      rootDistance: 0,
      x: apiNode.displayData?.position?.x,
      y: apiNode.displayData?.position?.y,
      vx: 0,
      vy: 0,
    };
  }

  /**
   * One past the highest stepOrder currently in use within a recipe — the
   * sensible default for any brand-new node (appended at the very end of
   * the preparation sequence; the user can drag it elsewhere once it's
   * actually part of something).
   */
  private nextStepOrder(recipeId: string): number {
    let max = -1;
    for (const node of this.state.nodePool.values()) {
      if (node.recipeId === recipeId) max = Math.max(max, node.data.stepOrder ?? 0);
    }
    return max + 1;
  }

  /**
   * Create a brand-new, blank leaf node (no ingredient/transformation, no
   * children) at the given position — the shape every "create node" flow
   * needs (drag-create, add-as-child, add-parent, transform, ...), so they
   * share one factory instead of each hand-building the same literal.
   * `id` defaults to a timestamp-based id; pass one explicitly for a
   * caller with its own id scheme (e.g. importing a subgraph).
   */
  createBlankNode(recipeId: string, x: number, y: number, id?: string): DisplayNode {
    const nodeId = id ?? `node_${Date.now()}`;
    const data: RecipeNode = {
      id: nodeId,
      name: undefined,
      children: [],
      recipeId,
      stepOrder: this.nextStepOrder(recipeId),
    };
    return {
      id: nodeId,
      name: '(nou)',
      type: 'ingredient',
      data,
      recipeId,
      isMainRoot: false,
      leafDistance: 0,
      rootDistance: 0,
      x,
      y,
      vx: 0,
      vy: 0,
    };
  }

  /**
   * Create a DisplayNode for a recipe's main root (browse mode).
   *
   * Root nodes aren't linked to each other, and the repulsion force only
   * kicks in once nodes are closer than its minDistance — so with `total`
   * nodes in a single row spacingX apart, the initial layout IS the final
   * layout. Lay them out on a triangular (hex-like) lattice — alternating
   * rows offset by half a column — so browsing many recipes reads as a
   * scatter rather than a rigid grid or a single line.
   */
  private createMainRootNode(item: RecipeListItem, index: number, total: number): DisplayNode {
    const spacingX = 220;
    const spacingY = 170;
    const centerX = 400;
    const startY = 150;

    const columns = Math.max(1, Math.ceil(Math.sqrt(total)));
    const row = Math.floor(index / columns);
    const col = index % columns;
    const itemsInRow = Math.min(columns, total - row * columns);
    const rowStartX = centerX - ((itemsInRow - 1) * spacingX) / 2;
    // Stagger every other row by half a column for a triangular packing.
    const stagger = row % 2 === 1 ? spacingX / 2 : 0;

    return {
      id: item.mainRootNodeId,
      name: item.title,
      type: 'ingredient' as DisplayNode['type'],
      data: {
        id: item.mainRootNodeId,
        name: item.title,
        description: item.description,
        imageUrl: item.imageUrl,
        children: [],
        stepOrder: 0,
      } as RecipeNode,
      recipeId: item.id,
      isMainRoot: true,
      leafDistance: 0,
      rootDistance: 0,
      x: rowStartX + col * spacingX + stagger,
      y: startY + row * spacingY,
      vx: 0,
      vy: 0,
    };
  }

  // ============================================================================
  // Actions
  // ============================================================================

  selectRecipe(recipeId: string | null): void {
    if (this.state.selectedRecipeId === recipeId) {
      return; // No change
    }
    this.state.selectedRecipeId = recipeId;
    this.notify();
  }

  focusRecipe(recipeId: string | null): void {
    if (this.state.focusedRecipeId === recipeId) {
      return; // No change
    }

    if (recipeId) {
      // Focusing a recipe
      this.state.focusedRecipeId = recipeId;
      this.state.recipesHiddenByFocus.clear();

      // Hide nodes from other recipes (skip permanently/search-hidden)
      for (const [nodeId, node] of this.state.nodePool) {
        if (node.recipeId === recipeId) continue;
        if (this.state.hiddenRecipes.has(node.recipeId) || this.state.searchHiddenRecipes.has(node.recipeId)) continue;

        this.state.hiddenNodeIds.add(nodeId);
        this.state.recipesHiddenByFocus.add(node.recipeId);
      }

      // Show nodes from focused recipe
      for (const [nodeId, node] of this.state.nodePool) {
        if (node.recipeId === recipeId) {
          this.state.hiddenNodeIds.delete(nodeId);
        }
      }
    } else {
      // Unfocusing
      this.unfocusRecipe();
      return;
    }

    this.notify();
  }

  toggleNodeExpansion(nodeId: string): void {
    if (this.state.collapsedNodeIds.has(nodeId)) {
      this.state.collapsedNodeIds.delete(nodeId);
    } else {
      this.state.collapsedNodeIds.add(nodeId);
    }
    this.notify();
  }

  /**
   * Expand a node.
   *
   * - If it's the main root of a fully-loaded recipe, unhide everything for
   *   that recipe at once (covers isolated/disconnected nodes that a
   *   children-only cascade would miss).
   * - Else if its subgraph is already in the pool, unhide direct children
   *   and cascade into non-folded descendants.
   * - Else the subgraph hasn't been fetched yet: load it from the API,
   *   position and link the new nodes, and apply auto-fold to them.
   *
   * This is the one place expand logic lives — both GraphVisualizer and
   * ListView call it (List View's main-root case routes through
   * GraphVisualizer.toggleExpandNode() instead, for its unsaved-changes
   * guard, which calls back into this same method), so a node expands
   * identically regardless of which view triggered it.
   */
  async expandNode(nodeId: string): Promise<void> {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    if (!this.state.collapsedNodeIds.has(nodeId)) {
      return; // Already expanded, no change
    }

    if (node.isMainRoot && this.state.loadedRecipes.get(node.recipeId)?.fullyLoaded) {
      this.state.collapsedNodeIds.delete(nodeId);
      for (const other of this.state.nodePool.values()) {
        if (other.recipeId !== node.recipeId || other.id === nodeId) continue;
        this.state.hiddenNodeIds.delete(other.id);
      }
      this.focusRecipe(node.recipeId);
      this.selectNodeWithAutoRecipe(nodeId);
      this.syncMainRootFoldState(node.recipeId);
      this.notify();
      return;
    }

    const existingDescendants = this.getDescendantsInPool(node);
    if (existingDescendants.length > 0) {
      this.state.collapsedNodeIds.delete(nodeId);

      const parentMap = this._buildParentMap();
      const newlyUnhidden = new Set<string>();

      for (const childId of (node.data.children || [])) {
        if (!this.state.hiddenNodeIds.has(childId)) continue;
        this.state.hiddenNodeIds.delete(childId);
        newlyUnhidden.add(childId);
      }

      const queue = Array.from(newlyUnhidden);
      while (queue.length > 0) {
        const uid = queue.shift()!;
        if (this.state.collapsedNodeIds.has(uid)) continue;
        const uNode = this.state.nodePool.get(uid);
        if (!uNode) continue;
        for (const childId of (uNode.data.children || [])) {
          if (!this.state.hiddenNodeIds.has(childId)) continue;
          this.state.hiddenNodeIds.delete(childId);
          newlyUnhidden.add(childId);
          queue.push(childId);
        }
      }

      this._propagateAutoUnfold(newlyUnhidden, parentMap);

      if (node.isMainRoot) {
        this._applyAutoFold();
        this.focusRecipe(node.recipeId);
        this.selectNodeWithAutoRecipe(nodeId);
      }

      this.syncMainRootFoldState(node.recipeId);
      // This path is reachable with node.isMainRoot true (a partially-
      // loaded main root whose subgraph is already in the pool) — main
      // root fold state is never trackable, so check explicitly rather
      // than assuming the fast path above already excluded it.
      if (!node.isMainRoot) this.trackGraphIfEditing(node.recipeId);
      this.notify();
      return;
    }

    // Subgraph not loaded yet — fetch it from the API.
    try {
      const { getNodeSubgraph } = await import('../api/index.js');
      const response = await getNodeSubgraph(nodeId);
      const subgraphNodes = response.nodes;

      const newNodes: DisplayNode[] = [];
      for (const [nid, recipeNode] of Object.entries(subgraphNodes)) {
        if (nid === nodeId) continue; // Root already exists, updated below
        newNodes.push(this.createDisplayNodeFromApi(recipeNode, node.recipeId, false));
      }

      // Propagate inactive state from the parent (if parent is dimmed, children should be too)
      if (node.__inactive) {
        for (const n of newNodes) n.__inactive = true;
      }

      this._computeNodeLevels(newNodes);

      // Assign positions, respecting saved offsets relative to the expanded parent.
      const needsLayout: DisplayNode[] = [];
      for (const nn of newNodes) {
        const offsetX = nn.data.displayData?.position?.x;
        const offsetY = nn.data.displayData?.position?.y;
        if (offsetX != null && offsetY != null) {
          nn.x = (node.x ?? 400) + offsetX;
          nn.y = (node.y ?? 100) + offsetY;
        } else {
          needsLayout.push(nn);
        }
      }
      if (needsLayout.length > 0) {
        this._assignTreeLayout(node, needsLayout);
      }

      for (const nn of newNodes) {
        this.state.nodePool.set(nn.id, nn);
      }

      this._applyAutoFold(newNodes);

      // Create links from children relationships
      const newLinks: Link<DisplayNode>[] = [];
      for (const [nid, apiNode] of Object.entries(subgraphNodes)) {
        if (nid === nodeId) continue;
        for (const childId of apiNode.children) {
          if (childId === nodeId) continue; // Skip back-reference to root
          const sourceNode = this.state.nodePool.get(nid);
          const targetNode = this.state.nodePool.get(childId);
          if (sourceNode && targetNode) {
            newLinks.push({ source: sourceNode, target: targetNode, shape: { x: 0, y: 0 }, active: true });
          }
        }
      }

      // The root node was created from search results (minimal). The subgraph
      // response carries the complete node — apply all fields so the root
      // reflects its persisted state after a page reload.
      const rootApiNode = subgraphNodes[nodeId];
      if (rootApiNode) {
        node.data = {
          ...node.data,
          children: rootApiNode.children,
          ingredientInfo: rootApiNode.ingredientInfo,
          transformationInfos: rootApiNode.transformationInfos,
          imageUrl: rootApiNode.imageUrl,
          description: rootApiNode.description,
          name: rootApiNode.name,
        };
        node.name = resolveNodeTitle(rootApiNode);
        node.type = this.getNodeType(rootApiNode);

        for (const childId of rootApiNode.children) {
          const targetNode = this.state.nodePool.get(childId);
          if (targetNode) {
            newLinks.push({ source: node, target: targetNode, shape: { x: 0, y: 0 }, active: true });
          }
        }
      }

      for (const link of newLinks) assignLinkShape(link);
      this.addLinks(newLinks);

      this.state.collapsedNodeIds.delete(nodeId);

      // Mark recipe as fully loaded if this is a main root
      if (node.isMainRoot) {
        const recipeEntry = this.state.loadedRecipes.get(node.recipeId);
        if (recipeEntry) recipeEntry.fullyLoaded = true;
        this.focusRecipe(node.recipeId);
        this.selectNodeWithAutoRecipe(nodeId);
      }

      this.syncMainRootFoldState(node.recipeId);
      if (!node.isMainRoot) this.trackGraphIfEditing(node.recipeId);
      this.notify();
    } catch (error) {
      console.error('Failed to expand node:', nodeId, error);
      throw error;
    }
  }

  /**
   * Collapse a node.
   *
   * Folding the main root hides ALL of the recipe's other nodes at once
   * (including isolated/disconnected nodes a children-cascade would miss)
   * and drops focus back to browsing all recipes. Otherwise, hide direct
   * children, cascade into descendants whose every parent is now hidden,
   * and auto-fold any ancestor that just lost its last visible child.
   */
  collapseNode(nodeId: string): void {
    if (this.state.collapsedNodeIds.has(nodeId)) {
      return; // Already collapsed, no change
    }
    const node = this.state.nodePool.get(nodeId);

    if (node?.isMainRoot) {
      this.state.collapsedNodeIds.add(nodeId);
      for (const other of this.state.nodePool.values()) {
        if (other.recipeId !== node.recipeId || other.id === nodeId) continue;
        this.state.hiddenNodeIds.add(other.id);
      }
      this.unfocusRecipe();
      return;
    }

    this.state.collapsedNodeIds.add(nodeId);

    if (node) {
      const children = node.data.children || [];
      const parentMap = this._buildParentMap();
      const newlyHidden = new Set<string>();
      for (const childId of children) {
        if (this.state.hiddenNodeIds.has(childId)) continue;
        this.state.hiddenNodeIds.add(childId);
        newlyHidden.add(childId);
      }

      const queue = Array.from(newlyHidden);
      while (queue.length > 0) {
        const hiddenId = queue.shift()!;
        const hiddenNode = this.state.nodePool.get(hiddenId);
        if (!hiddenNode) continue;
        for (const childId of (hiddenNode.data.children || [])) {
          if (this.state.hiddenNodeIds.has(childId)) continue;
          if (this._allParentsHidden(childId, newlyHidden, parentMap)) {
            this.state.hiddenNodeIds.add(childId);
            newlyHidden.add(childId);
            queue.push(childId);
          }
        }
      }

      this._propagateAutoFold(newlyHidden, parentMap);
      this.syncMainRootFoldState(node.recipeId);
      // Reaching here already implies !node.isMainRoot (that path returned
      // above) and a real fold-state change (the guard at the top of this
      // method already excluded a no-op collapse).
      this.trackGraphIfEditing(node.recipeId);
    }

    this.notify();
  }

  /** Build a map from child ID to array of parent IDs */
  private _buildParentMap(): Map<string, string[]> {
    const parentMap = new Map<string, string[]>();
    for (const [nodeId, node] of this.state.nodePool) {
      for (const childId of (node.data.children || [])) {
        if (!parentMap.has(childId)) parentMap.set(childId, []);
        parentMap.get(childId)!.push(nodeId);
      }
    }
    return parentMap;
  }

  /** Check if all parents of a child are hidden */
  private _allParentsHidden(
    childId: string,
    hiddenSet: Set<string>,
    parentMap: Map<string, string[]>,
  ): boolean {
    const parents = parentMap.get(childId) || [];
    for (const parentId of parents) {
      if (!hiddenSet.has(parentId)) {
        const parent = this.state.nodePool.get(parentId);
        if (parent && !this.state.hiddenNodeIds.has(parentId)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * After a fold operation, auto-fold any parent that has lost all its
   * visible children.
   */
  private _propagateAutoFold(newlyHidden: Set<string>, parentMap: Map<string, string[]>): void {
    const checked = new Set<string>();
    for (const childId of newlyHidden) {
      const parents = parentMap.get(childId) || [];
      for (const parentId of parents) {
        if (checked.has(parentId)) continue;
        checked.add(parentId);
        const parent = this.state.nodePool.get(parentId);
        // Skip parents that are themselves folded or hidden already
        if (!parent || this.state.collapsedNodeIds.has(parent.id) || this.state.hiddenNodeIds.has(parent.id)) continue;
        let allHidden = true;
        for (const cid of (parent.data.children || [])) {
          const child = this.state.nodePool.get(cid);
          if (child && !this.state.hiddenNodeIds.has(child.id)) {
            allHidden = false;
            break;
          }
        }
        if (allHidden) {
          this.state.collapsedNodeIds.add(parent.id);
        }
      }
    }
  }

  /**
   * After an unfold operation, auto-unfold any parent whose children are
   * all now visible.
   */
  private _propagateAutoUnfold(newlyUnhidden: Set<string>, parentMap: Map<string, string[]>): void {
    const checked = new Set<string>();
    for (const childId of newlyUnhidden) {
      const parents = parentMap.get(childId) || [];
      for (const parentId of parents) {
        if (checked.has(parentId)) continue;
        checked.add(parentId);
        const parent = this.state.nodePool.get(parentId);
        // Only act on parents that are currently folded (and visible)
        if (!parent || !this.state.collapsedNodeIds.has(parent.id) || this.state.hiddenNodeIds.has(parent.id)) continue;
        let allVisible = true;
        for (const cid of (parent.data.children || [])) {
          const child = this.state.nodePool.get(cid);
          if (child && this.state.hiddenNodeIds.has(child.id)) {
            allVisible = false;
            break;
          }
        }
        if (allVisible) {
          this.state.collapsedNodeIds.delete(parent.id);
        }
      }
    }
  }

  /**
   * Hide descendants of nodes marked as folded by default (autoFold = true).
   * If a node list is provided, only checks those nodes (for freshly-loaded
   * subgraphs); otherwise walks every node in the pool.
   */
  private _applyAutoFold(nodes?: DisplayNode[]): void {
    const targets = nodes ?? Array.from(this.state.nodePool.values());
    for (const node of targets) {
      if (node.data.autoFold) {
        this._hideDescendantsRecursive(node);
      }
    }
  }

  /** Compute leafDistance and rootDistance for a set of nodes (pure graph topology). */
  private _computeNodeLevels(nodes: DisplayNode[]): void {
    if (nodes.length === 0) return;

    const childrenMap = new Map<string, DisplayNode[]>();
    const parentsMap = new Map<string, DisplayNode[]>();

    for (const node of nodes) {
      const children = (node.data.children || [])
        .map(cid => nodes.find(n => n.id === cid))
        .filter((n): n is DisplayNode => n !== undefined);
      childrenMap.set(node.id, children);
    }

    childrenMap.forEach((children, parentId) => {
      for (const child of children) {
        if (!parentsMap.has(child.id)) {
          parentsMap.set(child.id, []);
        }
        const parent = nodes.find(n => n.id === parentId);
        if (parent) {
          parentsMap.get(child.id)!.push(parent);
        }
      }
    });

    const leafNodes = nodes.filter(n => (childrenMap.get(n.id)?.length ?? 0) === 0);
    const rootNodes = nodes.filter(n => (parentsMap.get(n.id)?.length ?? 0) === 0);

    for (const node of nodes) {
      node.leafDistance = 0;
      node.rootDistance = 0;
    }

    const leafQueue = [...leafNodes];
    while (leafQueue.length > 0) {
      const current = leafQueue.shift()!;
      const parents = parentsMap.get(current.id) ?? [];
      for (const parent of parents) {
        const newDist = current.leafDistance + 1;
        if (newDist > parent.leafDistance) {
          parent.leafDistance = newDist;
          leafQueue.push(parent);
        }
      }
    }

    const rootQueue = [...rootNodes];
    while (rootQueue.length > 0) {
      const current = rootQueue.shift()!;
      const children = childrenMap.get(current.id) ?? [];
      for (const child of children) {
        const newDist = current.rootDistance + 1;
        if (newDist > child.rootDistance) {
          child.rootDistance = newDist;
          rootQueue.push(child);
        }
      }
    }
  }

  /** Lay out newly-fetched children under an expanded parent, grouped by tree depth. */
  private _assignTreeLayout(parent: DisplayNode, newNodes: DisplayNode[]): void {
    const parentX = parent.x ?? 400;
    const parentY = parent.y ?? 100;
    const levelHeight = 120;
    const nodeDiameter = NODE_RADIUS * 2;
    const spacing = 20;

    const groups = new Map<number, DisplayNode[]>();
    for (const node of newNodes) {
      const dist = node.rootDistance;
      if (!groups.has(dist)) groups.set(dist, []);
      groups.get(dist)!.push(node);
    }

    const sortedLevels = Array.from(groups.keys()).sort((a, b) => a - b);
    for (const distance of sortedLevels) {
      const nodes = groups.get(distance)!;
      const y = parentY + (distance + 1) * levelHeight;
      const totalWidth = nodes.length * nodeDiameter + (nodes.length - 1) * spacing;
      const startX = parentX - totalWidth / 2 + nodeDiameter / 2;
      nodes.forEach((node, index) => {
        node.x = startX + index * (nodeDiameter + spacing);
        node.y = y;
      });
    }
  }

  /** Assign initial tree positions for a freshly-loaded recipe's nodes (loadFullRecipe). */
  private _assignTreePositions(nodes: DisplayNode[]): void {
    const groups = new Map<number, DisplayNode[]>();
    for (const node of nodes) {
      const dist = node.rootDistance;
      if (!groups.has(dist)) groups.set(dist, []);
      groups.get(dist)!.push(node);
    }

    const levelHeight = 120;
    const nodeDiameter = NODE_RADIUS * 2;
    const spacing = 20;
    const defaultCenterX = 400;

    const sortedLevels = Array.from(groups.keys()).sort((a, b) => a - b);
    for (const distance of sortedLevels) {
      const levelNodes = groups.get(distance)!;
      const y = 100 + distance * levelHeight;

      if (distance === 0) {
        const total = levelNodes.length;
        const totalWidth = total * nodeDiameter + (total - 1) * spacing;
        const startX = defaultCenterX - totalWidth / 2 + nodeDiameter / 2;
        levelNodes.forEach((node, index) => {
          if (node.x == null) node.x = startX + index * (nodeDiameter + spacing);
          node.y = y;
        });
      } else {
        for (const node of levelNodes) {
          if (node.x == null) node.x = defaultCenterX;
          node.y = y;
        }
      }
    }
  }

  /**
   * Reconcile a recipe's main root fold flag with reality: collapsed if none
   * of its other nodes are visible, expanded if at least one is. Call this
   * after any operation that adds, removes, hides, or shows nodes so the
   * main root's fold state — which both views read independently — never
   * drifts from what's actually on screen.
   */
  syncMainRootFoldState(recipeId: string): void {
    const entry = this.state.loadedRecipes.get(recipeId);
    if (!entry) return;
    const mainRootId = entry.rootNodeIds[entry.mainRootIndex];
    if (!mainRootId) return;
    const mainRoot = this.state.nodePool.get(mainRootId);
    if (!mainRoot) return;

    let hasNonRootNodes = false;
    let anyVisible = false;
    for (const node of this.state.nodePool.values()) {
      if (node.recipeId !== recipeId || node.isMainRoot) continue;
      hasNonRootNodes = true;
      if (!this.state.hiddenNodeIds.has(node.id)) {
        anyVisible = true;
        break;
      }
    }
    // No non-root nodes at all: the main root stands alone, leave its fold
    // state as-is rather than forcing it collapsed.
    if (!hasNonRootNodes) return;

    const shouldBeFolded = !anyVisible;
    const isFolded = this.state.collapsedNodeIds.has(mainRootId);
    if (isFolded !== shouldBeFolded) {
      if (shouldBeFolded) {
        this.state.collapsedNodeIds.add(mainRootId);
      } else {
        this.state.collapsedNodeIds.delete(mainRootId);
      }
      this.notify();
    }
  }

  expandAllNodes(): void {
    if (this.state.collapsedNodeIds.size === 0) {
      return; // No change
    }
    this.state.collapsedNodeIds.clear();
    this.notify();
  }

  selectNode(nodeId: string, multiSelect: boolean = false): void {
    if (!multiSelect) {
      // Single select: clear previous selection
      if (this.state.selectedNodeIds.size === 1 && this.state.selectedNodeIds.has(nodeId)) {
        return; // Already selected, no change
      }
      this.state.selectedNodeIds.clear();
      this.state.selectedNodeIds.add(nodeId);
    } else {
      // Multi-select: toggle selection
      if (this.state.selectedNodeIds.has(nodeId)) {
        this.state.selectedNodeIds.delete(nodeId);
      } else {
        this.state.selectedNodeIds.add(nodeId);
      }
    }
    this.notify();
  }

  deselectAllNodes(): void {
    if (this.state.selectedNodeIds.size === 0) {
      return; // No change
    }
    this.state.selectedNodeIds.clear();
    this.notify();
  }

  hideNode(nodeId: string): void {
    if (!this.state.hiddenNodeIds.has(nodeId)) {
      this.state.hiddenNodeIds.add(nodeId);
      this.notify();
    }
  }

  showNode(nodeId: string): void {
    if (this.state.hiddenNodeIds.has(nodeId)) {
      this.state.hiddenNodeIds.delete(nodeId);
      this.notify();
    }
  }

  hideNodes(nodeIds: string[]): void {
    let changed = false;
    for (const nodeId of nodeIds) {
      if (!this.state.hiddenNodeIds.has(nodeId)) {
        this.state.hiddenNodeIds.add(nodeId);
        changed = true;
      }
    }
    if (changed) {
      this.notify();
    }
  }

  showNodes(nodeIds: string[]): void {
    let changed = false;
    for (const nodeId of nodeIds) {
      if (this.state.hiddenNodeIds.has(nodeId)) {
        this.state.hiddenNodeIds.delete(nodeId);
        changed = true;
      }
    }
    if (changed) {
      this.notify();
    }
  }

  hideRecipeNodes(recipeId: string): void {
    let changed = false;
    for (const [nodeId, node] of this.state.nodePool) {
      if (node.recipeId === recipeId && !this.state.hiddenNodeIds.has(nodeId)) {
        this.state.hiddenNodeIds.add(nodeId);
        changed = true;
      }
    }
    if (changed) {
      this.notify();
    }
  }

  showRecipeNodes(recipeId: string): void {
    let changed = false;
    for (const [nodeId, node] of this.state.nodePool) {
      if (node.recipeId === recipeId && this.state.hiddenNodeIds.has(nodeId)) {
        this.state.hiddenNodeIds.delete(nodeId);
        changed = true;
      }
    }
    if (changed) {
      this.notify();
    }
  }

  setEditMode(editMode: boolean): void {
    if (this.state.editMode === editMode) {
      return; // No change
    }
    this.state.editMode = editMode;
    this.notify();
  }

  setHoveredNode(nodeId: string | null): void {
    if (this.state.hoveredNodeId === nodeId) {
      return; // No change
    }
    this.state.hoveredNodeId = nodeId;
    this.notify();
  }

  // ============================================================================
  // Data Management
  // ============================================================================

  addNodes(nodes: DisplayNode[]): void {
    for (const node of nodes) {
      this.state.nodePool.set(node.id, node);
      this.trackGraphIfEditing(node.recipeId);
    }
    this.notify();
  }

  updateNode(nodeId: string, updates: Partial<DisplayNode>): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) {
      console.warn(`Cannot update non-existent node: ${nodeId}`);
      return;
    }
    // Merge updates into existing node
    const updatedNode = { ...node, ...updates };
    this.state.nodePool.set(nodeId, updatedNode);
    this.notify();
  }

  removeNode(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) {
      return; // No change
    }
    this.state.nodePool.delete(nodeId);
    // Also remove from selection and collapsed state if present
    this.state.selectedNodeIds.delete(nodeId);
    this.state.collapsedNodeIds.delete(nodeId);
    this.trackGraphIfEditing(node.recipeId);
    this.notify();
  }

  clearNodes(): void {
    if (this.state.nodePool.size === 0) {
      return; // No change
    }
    this.state.nodePool.clear();
    this.state.selectedNodeIds.clear();
    this.state.collapsedNodeIds.clear();
    this.notify();
  }

  // ============================================================================
  // Link Management
  // ============================================================================

  addLinks(links: Link<DisplayNode>[]): void {
    // Check for duplicates before adding
    for (const link of links) {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      const exists = this.state.linkPool.some(existingLink => {
        const existingSourceId = typeof existingLink.source === 'object' ? existingLink.source.id : existingLink.source;
        const existingTargetId = typeof existingLink.target === 'object' ? existingLink.target.id : existingLink.target;
        return existingSourceId === sourceId && existingTargetId === targetId;
      });

      if (!exists) {
        this.state.linkPool.push(link);
        // Only track for links actually added — a call with an
        // already-existing link (e.g. completing a link flow twice) must
        // not falsely dirty the tracker.
        const recipeId = typeof link.source === 'object' ? link.source.recipeId : this.state.nodePool.get(sourceId)?.recipeId;
        if (recipeId) this.trackGraphIfEditing(recipeId);
      }
    }
    this.notify();
  }

  setLinkPool(links: Link<DisplayNode>[]): void {
    // Replace the entire link pool. Used for both surgical single-link
    // edits (filtering out one link) and full rebuilds (e.g. discard
    // restoring a recipe's links from scratch) — since the caller doesn't
    // say which, and the new array can span every loaded recipe, diff the
    // tracked recipe's own subset of links before/after and track only if
    // that subset actually changed. Skipped entirely when nothing is being
    // tracked (the common non-edit-mode case).
    if (this.trackedRecipeId) {
      const recipeId = this.trackedRecipeId;
      const key = (l: Link<DisplayNode>) => `${l.source.id}->${l.target.id}`;
      const before = new Set(
        this.state.linkPool
          .filter(l => l.source.recipeId === recipeId || l.target.recipeId === recipeId)
          .map(key)
      );
      const after = new Set(
        links
          .filter(l => l.source.recipeId === recipeId || l.target.recipeId === recipeId)
          .map(key)
      );
      const changed = before.size !== after.size || [...before].some(k => !after.has(k));
      if (changed) this.trackGraphIfEditing(recipeId);
    }
    this.state.linkPool = links;
    this.notify();
  }

  removeLink(sourceId: string, targetId: string): void {
    const initialLength = this.state.linkPool.length;
    this.state.linkPool = this.state.linkPool.filter(link => {
      const linkSourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const linkTargetId = typeof link.target === 'object' ? link.target.id : link.target;
      return !(linkSourceId === sourceId && linkTargetId === targetId);
    });
    
    if (this.state.linkPool.length !== initialLength) {
      this.notify();
    }
  }

  clearLinks(): void {
    if (this.state.linkPool.length === 0) {
      return; // No change
    }
    this.state.linkPool = [];
    this.notify();
  }

  // ============================================================================
  // Recipe Management
  // ============================================================================

  addRecipe(recipeId: string, entry: RecipeEntry): void {
    this.state.loadedRecipes.set(recipeId, entry);
    this.notify();
  }

  removeRecipe(recipeId: string): void {
    if (!this.state.loadedRecipes.has(recipeId)) {
      return; // No change
    }

    // If this was the focused recipe, unfocus properly — restoring the
    // other recipes it hid — rather than just nulling focusedRecipeId.
    // Otherwise those recipes stay stuck in hiddenNodeIds forever: with
    // focusedRecipeId already null, a later unfocusRecipe() call (e.g. from
    // folding this recipe back after a reload) would just no-op instead of
    // restoring them.
    if (this.state.focusedRecipeId === recipeId) {
      this.unfocusRecipe();
    }

    this.state.loadedRecipes.delete(recipeId);

    // If this was the selected recipe, deselect it
    if (this.state.selectedRecipeId === recipeId) {
      this.state.selectedRecipeId = null;
    }

    this.notify();
  }

  clearRecipes(): void {
    if (this.state.loadedRecipes.size === 0) {
      return; // No change
    }
    this.state.loadedRecipes.clear();
    this.state.selectedRecipeId = null;
    this.state.focusedRecipeId = null;
    this.notify();
  }

  // ============================================================================
  // Edit Mode State Methods
  // ============================================================================

  setEditionSubMode(mode: EditionMode): void {
    if (this.state.editionSubMode === mode) {
      return; // No change
    }
    this.state.editionSubMode = mode;
    this.notify();
  }

  // ============================================================================
  // Hidden Recipes Methods
  // ============================================================================

  hideRecipe(recipeId: string): void {
    if (this.state.hiddenRecipes.has(recipeId)) {
      return; // Already hidden
    }
    this.state.hiddenRecipes.add(recipeId);
    this.state.recipesHiddenByFocus.add(recipeId);

    // Hide all nodes of this recipe
    for (const [nodeId, node] of this.state.nodePool) {
      if (node.recipeId === recipeId) {
        this.state.hiddenNodeIds.add(nodeId);
      }
    }

    this.notify();
  }

  showRecipe(recipeId: string): void {
    if (!this.state.hiddenRecipes.has(recipeId)) {
      return; // Not hidden
    }
    this.state.hiddenRecipes.delete(recipeId);
    this.state.recipesHiddenByFocus.delete(recipeId);

    // Show all nodes of this recipe
    for (const [nodeId, node] of this.state.nodePool) {
      if (node.recipeId === recipeId) {
        this.state.hiddenNodeIds.delete(nodeId);
      }
    }

    this.notify();
  }

  restoreRecipe(recipeId: string): void {
    if (!this.state.hiddenRecipes.has(recipeId)) {
      return; // Not hidden
    }
    this.state.hiddenRecipes.delete(recipeId);
    this.state.recipesHiddenByFocus.delete(recipeId);

    // Show main root in collapsed state
    const entry = this.state.loadedRecipes.get(recipeId);
    if (entry) {
      const mainRootId = entry.rootNodeIds[entry.mainRootIndex];
      this.state.hiddenNodeIds.delete(mainRootId);
      this.state.collapsedNodeIds.add(mainRootId);
    }

    this.notify();
  }

  // ============================================================================
  // Search Results Methods
  // ============================================================================

  /**
   * Apply ingredient-search results to the graph.
   *
   * Recipes that match stay visible (keeping their positions); new
   * matching recipes are added as root nodes and background-loaded;
   * non-matching recipes already in the pool are hidden (any depth, not
   * just the main root and its direct children) rather than removed, so
   * they come straight back — still expanded/collapsed as they were — the
   * next time the filter matches them again.
   *
   * This is the single implementation both GraphVisualizer and (should it
   * ever grow its own search box) ListView must call — the previous
   * GraphVisualizer-only copy hid every node of a non-matching recipe,
   * while a stale, never-called copy of this method only hid the main
   * root's direct children, silently leaving deeper nodes visible.
   */
  async applySearchResults(items: RecipeListItem[]): Promise<void> {
    // Restore recipes hidden by a previous filter to their default
    // browse-mode state (root visible+collapsed, children hidden) before
    // applying the new one.
    for (const node of this.state.nodePool.values()) {
      if (!this.state.searchHiddenRecipes.has(node.recipeId)) continue;
      if (node.isMainRoot) {
        this.state.hiddenNodeIds.delete(node.id);
        this.state.collapsedNodeIds.add(node.id);
      } else {
        this.state.hiddenNodeIds.add(node.id);
      }
    }
    this.state.searchHiddenRecipes.clear();

    const matchingIds = new Set(items.map(r => r.id));
    // Never hide the currently focused recipe, even if it isn't among the
    // given items — focus means "show just this one regardless of the
    // browse/search list", and a recipe easily ends up outside it without
    // that meaning the user wants it hidden (deep-linked directly rather
    // than reached by browsing, or simply not among the most-recently-
    // updated N the empty-filter "restore" path re-fetches). Without this,
    // a focused recipe could vanish the moment this ran — e.g. the
    // momentary unfocus/refocus a reload cycle does (see
    // reapplyFocusImmediately()) fires the same restore path in the
    // background, whose result can land after the refocus and wipe the
    // recipe straight back out.
    if (this.state.focusedRecipeId) {
      matchingIds.add(this.state.focusedRecipeId);
    }
    const existingRecipeIds = new Set<string>();
    for (const node of this.state.nodePool.values()) {
      existingRecipeIds.add(node.recipeId);
    }

    // Hide every node (any depth) of non-matching recipes still in the pool.
    for (const node of this.state.nodePool.values()) {
      if (matchingIds.has(node.recipeId)) continue;
      this.state.hiddenNodeIds.add(node.id);
      this.state.searchHiddenRecipes.add(node.recipeId);
    }

    // Add root nodes for newly-matching recipes not yet in the pool,
    // placed on a simple wrapping grid so they don't disturb the layout
    // of recipes already on screen.
    const newNodes: DisplayNode[] = [];
    let newIndex = this.state.nodePool.size;
    for (const item of items) {
      if (existingRecipeIds.has(item.id)) continue;

      const dnode: DisplayNode = {
        id: item.mainRootNodeId,
        name: item.title,
        type: 'ingredient',
        data: {
          id: item.mainRootNodeId,
          name: item.title,
          description: item.description,
          imageUrl: item.imageUrl,
          children: [],
          stepOrder: 0,
        } as RecipeNode,
        recipeId: item.id,
        isMainRoot: true,
        leafDistance: 0,
        rootDistance: 0,
        x: (newIndex % 10) * 180 + 100,
        y: 150 + (newIndex % 5) * 30,
        vx: 0,
        vy: 0,
      };
      newNodes.push(dnode);
      this.state.loadedRecipes.set(item.id, {
        rootNodeIds: item.rootNodeIds,
        mainRootIndex: item.mainRootIndex,
        fullyLoaded: false,
      });
      newIndex++;
    }

    if (newNodes.length > 0) {
      this.addNodes(newNodes);

      // addNodes() has no notion of focus — without this, a recipe added
      // here while another one is focused would flash into view alongside
      // it instead of staying hidden like every other non-focused recipe.
      if (this.state.focusedRecipeId) {
        for (const node of newNodes) {
          if (node.recipeId === this.state.focusedRecipeId) continue;
          this.state.hiddenNodeIds.add(node.id);
          this.state.recipesHiddenByFocus.add(node.recipeId);
        }
      }
    }

    this.notify();

    // Background-load full graph data for the newly-added recipes.
    for (const item of items) {
      if (existingRecipeIds.has(item.id)) continue;
      await this.backgroundLoadRecipeGraph(item.id);
    }
  }

  clearSearchResults(): void {
    // Show all previously search-hidden recipes
    for (const [nodeId, node] of this.state.nodePool) {
      if (this.state.searchHiddenRecipes.has(node.recipeId)) {
        if (node.isMainRoot) {
          this.state.hiddenNodeIds.delete(nodeId);
        }
      }
    }
    this.state.searchHiddenRecipes.clear();
    this.notify();
  }

  getSearchHiddenRecipes(): Set<string> {
    return new Set(this.state.searchHiddenRecipes);
  }

  // ============================================================================
  // ChangeTracker Methods
  // ============================================================================

  /**
   * Build a temporary DisplayGraph containing only the nodes of a given
   * recipe, for use as a ChangeTracker snapshot. Width/height are only
   * ever consulted by DisplayGraph to place nodes that have no position
   * of their own — never the case here, since every node's current x/y
   * is written into displayData.position below — so fixed placeholder
   * values are fine.
   */
  private buildRecipeGraph(recipeId: string): DisplayGraph {
    const nodes = Array.from(this.state.nodePool.values())
      .filter(n => n.recipeId === recipeId);

    const recipeEntry = this.state.loadedRecipes.get(recipeId);
    const recipeGraph: RecipeGraph = {
      rootNodeIds: recipeEntry?.rootNodeIds ?? [],
      nodes: {},
    };

    const mainRoot = nodes.find(n => n.isMainRoot);
    const mainRootX = mainRoot?.x ?? 0;
    const mainRootY = mainRoot?.y ?? 0;

    for (const dnode of nodes) {
      // Clone the RecipeNode data and include current position in
      // displayData. Positions are stored as RELATIVE offsets from the
      // main root.
      const clonedNode: RecipeNode = {
        ...dnode.data,
        children: [...dnode.data.children],
        displayData: {
          position: { x: (dnode.x ?? 0) - mainRootX, y: (dnode.y ?? 0) - mainRootY },
        },
      };
      recipeGraph.nodes[dnode.id] = clonedNode;
    }

    return new DisplayGraph(recipeGraph, 0, 0, recipeId);
  }

  /**
   * Snapshot the given recipe's current graph as the ChangeTracker's
   * baseline — call once the recipe is fully loaded, on entering edit
   * mode or focusing/switching to a recipe while already editing.
   */
  initializeChangeTracker(recipeId: string): void {
    this.changeTracker = new ChangeTracker(this.buildRecipeGraph(recipeId));
    this.trackedRecipeId = recipeId;
  }

  /** Re-snapshot after a successful save or a discard, so hasUnsavedChanges() goes back to false. */
  resetChangeTracker(recipeId: string): void {
    this.changeTracker?.reset(this.buildRecipeGraph(recipeId));
    this.trackedRecipeId = recipeId;
  }

  clearChangeTracker(): void {
    this.changeTracker = null;
    this.trackedRecipeId = null;
  }

  /**
   * Public entry point for tracking a node-level change from outside a
   * store mutation method — the one legitimate case is real-time keystroke
   * feedback (NodeVisualizer's name/description `input` handlers, which
   * mutate the in-memory node directly and want the Save button to appear
   * instantly, before the edit is actually committed via
   * handleNodeFieldChange() on blur). Every other mutation self-tracks
   * from inside its own store method — see trackNodeIfEditing()/
   * trackGraphIfEditing() below — and should not need this.
   */
  trackNodeChange(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (node) this.trackNodeIfEditing(node.recipeId, nodeId);
  }

  /**
   * Record a node-level change, scoped to the recipe currently being
   * tracked. A no-op if nothing is being tracked, or if recipeId isn't the
   * recipe changeTracker was initialized for (nodePool holds every loaded
   * recipe at once — a mutation on an unfocused/background recipe must
   * never dirty a different recipe's tracker).
   */
  private trackNodeIfEditing(recipeId: string, nodeId: string): void {
    if (this.changeTracker && this.trackedRecipeId === recipeId) {
      this.changeTracker.trackNodeChange(nodeId);
    }
  }

  /** Same as trackNodeIfEditing(), for changes not tied to a single node (structural changes: add/remove nodes or links, reparent, fold state, ...). */
  private trackGraphIfEditing(recipeId: string): void {
    if (this.changeTracker && this.trackedRecipeId === recipeId) {
      this.changeTracker.trackGraphChange();
    }
  }

  // ============================================================================
  // Event System
  // ============================================================================

  on<K extends keyof StoreEventMap>(event: K, callback: StoreEventMap[K]): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as (...args: unknown[]) => void);
  }

  off<K extends keyof StoreEventMap>(event: K, callback: StoreEventMap[K]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback as (...args: unknown[]) => void);
    }
  }

  emit<K extends keyof StoreEventMap>(event: K, payload: Parameters<StoreEventMap[K]>[0]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in store event handler for "${event}":`, error);
        }
      });
    }
  }

  // ============================================================================
  // Business Logic: Recipe Selection & Focus
  // ============================================================================

  async selectRecipeWithCheck(recipeId: string | null): Promise<boolean> {
    // Check for unsaved changes
    if (this.hasUnsavedChanges()) {
      this.emit('unsavedChangesDetected', {
        currentRecipeId: this.state.selectedRecipeId,
        targetRecipeId: recipeId,
      });
      return false;
    }

    // Perform the selection
    return this.doSelectRecipe(recipeId);
  }

  /** Internal: Perform recipe selection */
  private doSelectRecipe(recipeId: string | null): boolean {
    const previousId = this.state.selectedRecipeId;

    // Fold previous main root if deselecting
    if (recipeId === null && previousId !== null) {
      const previousEntry = this.state.loadedRecipes.get(previousId);
      if (previousEntry) {
        const previousRootId = previousEntry.rootNodeIds[previousEntry.mainRootIndex];
        if (this.isNodeExpanded(previousRootId)) {
          this.collapseNode(previousRootId);
        }
      }
    }

    // Update state
    this.state.selectedRecipeId = recipeId;

    // Focus/unfocus
    if (recipeId) {
      this.focusRecipe(recipeId);
    } else {
      this.unfocusRecipe();
    }

    // Update inactive states in edit mode
    if (this.state.editMode) {
      this.state.nodePool.forEach(node => {
        node.__inactive = recipeId !== null && node.recipeId !== recipeId;
      });
    }

    // Clear the change tracker when the recipe is deselected. Snapshot
    // creation on selecting/focusing a recipe is scoped to edit mode and
    // owned by the caller (see initializeChangeTracker()'s doc comment).
    if (!recipeId) {
      this.clearChangeTracker();
    }

    // Deselect node if it belonged to old recipe
    if (previousId !== null && recipeId === null) {
      const selectedNode = this.getSelectedNode();
      if (selectedNode?.recipeId === previousId) {
        this.deselectAllNodes();
      }
    }

    this.notify();
    this.emit('recipeChanged', { recipeId });
    return true;
  }

  selectNodeWithAutoRecipe(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;

    // Select the node
    this.state.selectedNodeIds.clear();
    this.state.selectedNodeIds.add(nodeId);

    // Auto-select the recipe
    this.state.selectedRecipeId = node.recipeId;

    this.notify();
    this.emit('nodeSelected', { nodeId });
  }

  setMultiSelectMode(enabled: boolean): void {
    if (this.state.multiSelectMode === enabled) return;
    this.state.multiSelectMode = enabled;
    // Leaving multi-select with more than one node selected would leave
    // the (multi-select-only) card view showing outside of multi-select —
    // collapse back to no selection instead.
    if (!enabled && this.state.selectedNodeIds.size > 1) {
      this.state.selectedNodeIds.clear();
    }
    this.notify();
  }

  setShowHiddenTools(enabled: boolean): void {
    if (this.state.showHiddenTools === enabled) return;
    this.state.showHiddenTools = enabled;
    this.notify();
  }

  /**
   * Toggle a node in/out of the current multi-selection (mode must
   * already be on — the caller decides whether to route a click here or
   * to the ordinary single-select path). Restricted to one recipe at a
   * time: toggling on a node from a *different* recipe than whatever's
   * already selected starts a fresh selection with just that node,
   * rather than mixing recipes.
   */
  toggleMultiSelectNode(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;

    const current = this.getSelectedNodes();
    if (current.length > 0 && current[0].recipeId !== node.recipeId) {
      this.state.selectedNodeIds.clear();
      this.state.selectedNodeIds.add(nodeId);
      this.state.selectedRecipeId = node.recipeId;
      this.notify();
      return;
    }

    if (!this.state.selectedRecipeId) {
      this.state.selectedRecipeId = node.recipeId;
    }
    this.selectNode(nodeId, true);
  }

  unfocusRecipe(): void {
    if (!this.state.focusedRecipeId) return;

    // Restore previously hidden recipes
    for (const [nodeId, node] of this.state.nodePool) {
      if (!this.state.recipesHiddenByFocus.has(node.recipeId)) continue;

      if (node.isMainRoot) {
        this.state.hiddenNodeIds.delete(nodeId);
        this.state.collapsedNodeIds.add(nodeId);
      } else {
        this.state.hiddenNodeIds.add(nodeId);
      }
    }

    this.state.recipesHiddenByFocus.clear();
    this.state.focusedRecipeId = null;

    this.notify();
  }

  deselect(): void {
    // Cancel any active flows (handled via editionSubMode)
    if (this.state.editionSubMode !== EditionMode.NONE) {
      this.state.pendingLink = null;
      this.state.editionSubMode = EditionMode.NONE;
      // Clear inactivation
      this.state.nodePool.forEach(node => {
        node.__inactive = false;
      });
    }

    // Deselecting a node never touches the selected *recipe* — selection
    // and focus are independent (see RecipeHeaderStrip/GraphVisualizer's
    // focus redesign): a folded/unfocused main root is not a reason to
    // drop the recipe from the header too.
    this.deselectAllNodes();
  }

  // ============================================================================
  // Business Logic: Data Loading
  // ============================================================================

  /**
   * Fetch a recipe's full graph and rebuild it in the pool as the visible,
   * selected recipe (used by discardChanges, restore, and promote-node
   * reloads). This is the single implementation of "load and show a full
   * recipe" — it must stay in sync with backgroundLoadRecipeGraph (which
   * loads the same data but keeps it hidden).
   *
   * Callers that need the reload to be visually seamless (no flash of the
   * recipe disappearing, no transient unfold/unfocus) — currently refresh
   * and save in GraphVisualizer — should call fetchRecipe() themselves
   * first, capture/clear/reapply view state, and only then call
   * applyLoadedRecipe() with the already-fetched data, so the entire
   * clear-and-rebuild happens synchronously with nothing awaited in
   * between. This combined method is fine for the other callers, where
   * there's no "old view" worth preserving smoothly.
   */
  async loadFullRecipe(recipeId: string): Promise<void> {
    try {
      const recipe = await this.fetchRecipe(recipeId);
      this.applyLoadedRecipe(recipeId, recipe);
    } catch (error) {
      console.error('Failed to load full recipe:', error);
    }
  }

  /** Fetch a recipe's full graph from the API without touching the store. */
  async fetchRecipe(recipeId: string): Promise<Recipe> {
    const { loadRecipe } = await import('../api/index.js');
    return loadRecipe(recipeId);
  }

  /**
   * Persist a recipe update and return the saved Recipe (the PUT response
   * already carries the full, server-assigned state) — doesn't touch the
   * store itself, so callers can rebuild the pool from the response
   * without a second network round trip. Mirrors fetchRecipe().
   */
  async submitRecipeUpdate(recipeId: string, payload: Record<string, unknown>): Promise<Recipe> {
    const { updateRecipe } = await import('../api/index.js');
    return updateRecipe(recipeId, payload);
  }

  /** Delete a recipe from the backend. Doesn't touch the store's own pool — callers remove the recipe's nodes themselves (see removeRecipe()/removeNodesFromPool()). */
  async deleteRecipe(recipeId: string): Promise<void> {
    const { deleteRecipe } = await import('../api/index.js');
    await deleteRecipe(recipeId);
  }

  /**
   * Synchronously rebuild the pool for `recipeId` from an already-fetched
   * Recipe (e.g. from fetchRecipe(), or the Recipe a save's PUT response
   * already carries) — no network access, so callers can sandwich this
   * between a clear and a view-state restore with no await in between,
   * guaranteeing the browser never paints an in-between state.
   */
  applyLoadedRecipe(recipeId: string, recipe: Recipe): void {
    try {
      if (!recipe.graph) return;

      const graph = recipe.graph;
      const rootNodeIds = graph.rootNodeIds;
      const mainRootIndex = recipe.mainRootIndex ?? 0;
      const nodes = graph.nodes;

      this.state.loadedRecipes.set(recipeId, {
        rootNodeIds,
        mainRootIndex,
        fullyLoaded: true,
      });

      const displayNodes: DisplayNode[] = Object.entries(nodes).map(([nodeId, apiNode]) => {
        const isMainRoot = rootNodeIds.length > 0 && rootNodeIds[mainRootIndex] === nodeId;
        return this.createDisplayNodeFromApi(apiNode, recipeId, isMainRoot);
      });

      this._computeNodeLevels(displayNodes);
      this._assignTreePositions(displayNodes);

      // Apply saved position offsets (relative to the main root) to children.
      const mainRootNode = displayNodes.find(n => n.isMainRoot);
      if (mainRootNode && mainRootNode.x != null && mainRootNode.y != null) {
        for (const dn of displayNodes) {
          if (dn.isMainRoot) continue;
          const offsetX = dn.data.displayData?.position?.x;
          const offsetY = dn.data.displayData?.position?.y;
          if (offsetX != null && offsetY != null) {
            dn.x = mainRootNode.x + offsetX;
            dn.y = mainRootNode.y + offsetY;
          }
        }
      }

      this.addNodes(displayNodes);

      // Create links from children relationships
      const links: Link<DisplayNode>[] = [];
      for (const apiNode of Object.values(nodes)) {
        for (const childId of apiNode.children) {
          const sourceNode = displayNodes.find(n => n.id === apiNode.id);
          const targetNode = displayNodes.find(n => n.id === childId);
          if (sourceNode && targetNode) {
            links.push({ source: sourceNode, target: targetNode, shape: { x: 0, y: 0 }, active: true });
          }
        }
      }
      for (const link of links) assignLinkShape(link);
      this.addLinks(links);

      // Apply auto-fold for nodes marked folded by default
      this._applyAutoFold(displayNodes);

      // Select main root so the side panel/header reflect the loaded recipe
      const mainRoot = displayNodes.find(n => n.isMainRoot);
      if (mainRoot) {
        this.selectNodeWithAutoRecipe(mainRoot.id);
      }

      this.syncMainRootFoldState(recipeId);
      this.notify();

      // Emit event so views can do any remaining view-only work (viewport fit, etc.)
      this.emit('recipeLoaded', { recipeId });
    } catch (error) {
      console.error('Failed to apply loaded recipe:', error);
    }
  }

  /**
   * Load a recipe's full graph into the pool without changing visual state.
   * The root keeps its current folded/visible state and every other node is
   * added hidden, so ingredient/transformation data is available for display
   * (side panel, hover cards) without expanding the recipe first.
   */
  async backgroundLoadRecipeGraph(recipeId: string): Promise<void> {
    const entry = this.state.loadedRecipes.get(recipeId);
    if (entry?.fullyLoaded) return; // Already fully loaded — nothing to do

    try {
      const { loadRecipe } = await import('../api/index.js');
      const recipe = await loadRecipe(recipeId);
      if (!recipe.graph) return;

      const graph = recipe.graph;
      const rootNodeIds = graph.rootNodeIds;
      const mainRootIndex = recipe.mainRootIndex ?? 0;
      const nodes = graph.nodes;

      this.state.loadedRecipes.set(recipeId, {
        rootNodeIds,
        mainRootIndex,
        fullyLoaded: true,
      });

      // Update existing (root) nodes with full data; create new hidden
      // DisplayNodes for children.
      const allNodes: DisplayNode[] = [];
      for (const [nodeId, apiNode] of Object.entries(nodes)) {
        const existing = this.state.nodePool.get(nodeId);
        if (existing) {
          existing.data = apiNode;
          existing.name = resolveNodeTitle(apiNode);
          existing.type = this.getNodeType(apiNode);
          allNodes.push(existing);
        } else {
          const dnode = this.createDisplayNodeFromApi(apiNode, recipeId, false);
          this.state.hiddenNodeIds.add(dnode.id); // Root is folded — children stay hidden
          this.state.nodePool.set(dnode.id, dnode);
          allNodes.push(dnode);
        }
      }

      this._computeNodeLevels(allNodes);

      // Position children as offsets relative to the main root.
      const mainRoot = this.state.nodePool.get(rootNodeIds[mainRootIndex]);
      if (mainRoot && mainRoot.x != null && mainRoot.y != null) {
        for (const dn of allNodes) {
          if (dn.id === mainRoot.id) continue;
          const offsetX = dn.data.displayData?.position?.x;
          const offsetY = dn.data.displayData?.position?.y;
          if (offsetX != null && offsetY != null) {
            dn.x = mainRoot.x + offsetX;
            dn.y = mainRoot.y + offsetY;
          }
        }
      }

      // Create links from children relationships
      const links: Link<DisplayNode>[] = [];
      for (const [nid, apiNode] of Object.entries(nodes)) {
        for (const childId of apiNode.children) {
          const sourceNode = this.state.nodePool.get(nid);
          const targetNode = this.state.nodePool.get(childId);
          if (sourceNode && targetNode) {
            links.push({ source: sourceNode, target: targetNode, shape: { x: 0, y: 0 }, active: true });
          }
        }
      }
      for (const link of links) assignLinkShape(link);
      this.addLinks(links);

      this.notify();
    } catch (error) {
      console.error('Failed to background-load recipe graph:', recipeId, error);
    }
  }

  async addRootNodes(items: RecipeListItem[]): Promise<void> {
    // Create DisplayNode for each item
    const displayNodes: DisplayNode[] = items.map((item, index) =>
      this.createMainRootNode(item, index, items.length)
    );

    // Track which recipes are loaded
    items.forEach(item => {
      this.state.loadedRecipes.set(item.id, {
        rootNodeIds: item.rootNodeIds,
        mainRootIndex: item.mainRootIndex,
        fullyLoaded: false,
      });
    });

    // Add to pool
    this.addNodes(displayNodes);

    // Root nodes start collapsed
    displayNodes.forEach(node => {
      this.collapseNode(node.id);
    });

    this.notify();

    // Fire background-load for each recipe so full graph data (ingredient
    // info, transformation info, children) is available for display without
    // waiting for an explicit expand.
    for (const item of items) {
      await this.backgroundLoadRecipeGraph(item.id);
    }
  }

  // ============================================================================
  // Business Logic: Edit Mode Management
  // ============================================================================

  enterEditMode(): void {
    this.state.editMode = true;

    // Update inactive states — dim nodes not from selected recipe
    const selectedRecipeId = this.state.selectedRecipeId;
    this.state.nodePool.forEach(node => {
      node.__inactive = selectedRecipeId !== null && node.recipeId !== selectedRecipeId;
    });

    this.notify();
    this.emit('editModeEntered', { recipeId: selectedRecipeId });
  }

  exitEditMode(): void {
    this.state.editMode = false;
    this.state.editionSubMode = EditionMode.NONE;
    this.state.pendingLink = null;

    // Clear inactive states
    this.state.nodePool.forEach(node => {
      node.__inactive = false;
    });

    // Clear change tracker
    this.clearChangeTracker();

    this.notify();
    this.emit('editModeExited', {});
  }

  handleNodeFieldChange(nodeId: string, field: string, value: unknown): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;

    if (field === 'name') {
      node.data.name = value as string | undefined;
      node.name = resolveNodeTitle(node.data);
    } else if (field === 'description') {
      node.data.description = value as string | undefined;
    } else if (field === 'imageUrl') {
      node.data.imageUrl = value as string | undefined;
    } else if (field === 'ingredientInfo.ingredient') {
      if (!value) {
        node.data.ingredientInfo = undefined;
      } else {
        node.data.ingredientInfo = node.data.ingredientInfo ?? { ingredient: value as any };
        node.data.ingredientInfo.ingredient = value as any;
      }
      node.name = resolveNodeTitle(node.data);
    } else if (field === 'ingredientInfo.description' && node.data.ingredientInfo) {
      node.data.ingredientInfo.description = value as string | undefined;
    } else if (field === 'ingredientInfo.quantity' && node.data.ingredientInfo) {
      node.data.ingredientInfo.quantity = value as any;
    } else if (field === 'transformationInfos') {
      // Whole-list replace — same reasoning as toolInfos below, now that a
      // node carries an ordered list instead of a single transformation.
      node.data.transformationInfos = value as any;
      node.name = resolveNodeTitle(node.data);
    } else if (field === 'toolInfos') {
      // Whole-list replace — simplest given the list is always small
      // (a handful of tool usages per node at most).
      node.data.toolInfos = value as any;
      node.name = resolveNodeTitle(node.data);
    } else if (field === 'hidden') {
      node.data.hidden = value as boolean;
    }

    // Track the change
    this.trackNodeIfEditing(node.recipeId, nodeId);
    this.notify();
  }

  // propagateEntityChange() below deliberately never tracks: it mutates
  // every node across every loaded recipe that references a shared
  // ingredient/transformation/tool whose canonical data changed elsewhere
  // (e.g. renamed from a different recipe's editor). That's an external
  // sync, not the user editing the currently-open recipe's own graph.
  propagateEntityChange(
    entityType: 'ingredient' | 'transformation' | 'tool',
    entityId: string,
    updatedEntity: unknown,
  ): void {
    this.state.nodePool.forEach(node => {
      if (entityType === 'ingredient') {
        const ingInfo = node.data.ingredientInfo;
        if (ingInfo?.ingredient?.id === entityId) {
          ingInfo.ingredient = updatedEntity as any;
          node.name = resolveNodeTitle(node.data);
        }
      } else if (entityType === 'transformation') {
        let changed = false;
        for (const trafoInfo of node.data.transformationInfos ?? []) {
          if (trafoInfo.transformation?.id === entityId) {
            trafoInfo.transformation = updatedEntity as any;
            changed = true;
          }
        }
        if (changed) node.name = resolveNodeTitle(node.data);
      } else if (entityType === 'tool') {
        let changed = false;
        for (const toolInfo of node.data.toolInfos ?? []) {
          if (toolInfo.tool.id === entityId) {
            toolInfo.tool = updatedEntity as any;
            changed = true;
          }
        }
        if (changed) node.name = resolveNodeTitle(node.data);
      }
    });
    this.notify();
  }

  buildSavePayload(recipeId: string): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    const selectedRecipe = this.state.loadedRecipes.get(recipeId);
    if (!selectedRecipe) return payload;

    // Find main root and extract name/description
    const mainRootId = selectedRecipe.rootNodeIds[selectedRecipe.mainRootIndex];
    const mainRoot = this.state.nodePool.get(mainRootId);
    if (mainRoot) {
      if (mainRoot.data.name) {
        payload.name = mainRoot.data.name;
      }
      if (mainRoot.data.description) {
        payload.description = mainRoot.data.description;
      }
    }

    // Compute root node IDs from current graph structure
    const computedRootIds = this.computeRootNodeIds(recipeId);
    const mainRootNode = Array.from(this.state.nodePool.values())
      .find(n => n.recipeId === recipeId && n.isMainRoot);
    payload.mainRootIndex = mainRootNode ? computedRootIds.indexOf(mainRootNode.id) : 0;
    if ((payload.mainRootIndex as number) < 0) payload.mainRootIndex = 0;

    // Collect all nodes of the selected recipe with positions as offsets
    const rootX = mainRoot?.x ?? 0;
    const rootY = mainRoot?.y ?? 0;
    const recipeNodes = Array.from(this.state.nodePool.values()).filter(n => n.recipeId === recipeId);
    payload.nodes = recipeNodes.map(dnode => {
      const nodeData = { ...dnode.data, children: [...(dnode.data.children || [])] } as any;
      nodeData.autoFold = !this.isNodeExpanded(dnode.id);
      nodeData.displayData = {
        position: {
          x: dnode.x != null ? dnode.x - rootX : 0,
          y: dnode.y != null ? dnode.y - rootY : 0,
        },
      };
      return nodeData;
    });

    return payload;
  }

  async saveRecipeChanges(): Promise<void> {
    const recipeId = this.state.selectedRecipeId;
    if (!recipeId || !this.hasUnsavedChanges()) return;

    try {
      // Build payload
      const payload = this.buildSavePayload(recipeId);
      if (Object.keys(payload).length === 0) return;

      // Send to API
      const { updateRecipe } = await import('../api/index.js');
      await updateRecipe(recipeId, payload);

      // Reset change tracker
      this.clearChangeTracker();

      this.emit('recipeSaved', { recipeId });
    } catch (error) {
      console.error('Failed to save recipe:', error);
    }
  }

  async discardChanges(): Promise<void> {
    const recipeId = this.state.selectedRecipeId;
    if (!recipeId) return;

    // Clear change tracker
    this.clearChangeTracker();

    // Reload recipe to restore original state
    await this.loadFullRecipe(recipeId);

    this.emit('changesDiscarded', { recipeId });
  }

  /** Compute root node IDs by finding nodes that no other node lists as a child */
  private computeRootNodeIds(recipeId: string): string[] {
    const recipeNodes = Array.from(this.state.nodePool.values()).filter(n => n.recipeId === recipeId);
    const childIds = new Set<string>();
    for (const node of recipeNodes) {
      for (const childId of (node.data.children || [])) {
        childIds.add(childId);
      }
    }
    return recipeNodes.filter(n => !childIds.has(n.id)).map(n => n.id);
  }

  // ============================================================================
  // Business Logic: Node Operations
  // ============================================================================

  handleMakeMainRoot(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;

    const recipeId = node.recipeId;
    const entry = this.state.loadedRecipes.get(recipeId);
    if (!entry) return;

    // Update main root index
    const newIndex = entry.rootNodeIds.indexOf(nodeId);
    entry.mainRootIndex = newIndex >= 0 ? newIndex : 0;

    // Update isMainRoot on all nodes of this recipe
    this.state.nodePool.forEach(n => {
      if (n.recipeId === recipeId) {
        n.isMainRoot = n.id === nodeId;
      }
    });

    // Track change if in edit mode
    this.trackGraphIfEditing(recipeId);

    this.notify();
  }

  /**
   * Remove a node's elaboration: strips its descendants, turning it back
   * into a bare leaf ingredient node — the node itself (and its ingredient
   * reference) stays, only its children subtree goes.
   */
  async handleRemoveElaboration(nodeId: string): Promise<void> {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;

    // Get all descendants (children, grandchildren, ...) — never nodeId itself
    const descendants = this.getDescendantsInPool(node);

    // Remove only the descendants from the pool
    this.removeNodesFromPool(descendants.map(n => n.id));

    // Detach the node's own children so it doesn't keep dangling
    // references to the descendants just removed
    node.data.children = [];

    // Track change if in edit mode
    this.trackGraphIfEditing(node.recipeId);

    this.notify();
  }

  // Deliberately never self-tracks and never calls notify() — it's a bulk
  // primitive shared by callers with opposite tracking needs:
  // handleRemoveElaboration() (a trackable edit, already tracks itself)
  // and GraphVisualizer's clearRecipe()/hideRecipe() (removing a whole
  // recipe from the pool, never trackable — those callers clear the
  // tracker outright instead). Callers are expected to notify().
  removeNodesFromPool(nodeIds: string[]): void {
    for (const id of nodeIds) {
      this.state.nodePool.delete(id);
      this.state.selectedNodeIds.delete(id);
      this.state.collapsedNodeIds.delete(id);
      this.state.hiddenNodeIds.delete(id);
    }

    // Remove links connected to these nodes
    this.state.linkPool = this.state.linkPool.filter(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      return !nodeIds.includes(sourceId) && !nodeIds.includes(targetId);
    });
  }

  /** Get all descendants of a node that exist in the pool (BFS) */
  getDescendantsInPool(node: DisplayNode): DisplayNode[] {
    const descendants: DisplayNode[] = [];
    const queue = [...(node.data.children || [])];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const childId = queue.shift()!;
      if (visited.has(childId)) continue;
      visited.add(childId);

      const childNode = this.state.nodePool.get(childId);
      if (childNode) {
        descendants.push(childNode);
        // BFS: add children of this child
        if (childNode.data.children) {
          queue.push(...childNode.data.children);
        }
      }
    }

    return descendants;
  }

  /** Get all ancestors of a node that exist in the pool (reverse of getDescendantsInPool, walking linkPool backwards). */
  getAncestorsInPool(node: DisplayNode): DisplayNode[] {
    const ancestors: DisplayNode[] = [];
    const visited = new Set<string>();

    const queue = this.state.linkPool
      .filter(l => l.target.id === node.id)
      .map(l => l.source.id);

    while (queue.length > 0) {
      const parentId = queue.shift()!;
      if (visited.has(parentId)) continue;
      visited.add(parentId);

      const parentNode = this.state.nodePool.get(parentId);
      if (parentNode) {
        ancestors.push(parentNode);
        for (const l of this.state.linkPool) {
          if (l.target.id === parentId) {
            queue.push(l.source.id);
          }
        }
      }
    }

    return ancestors;
  }

  hideDescendants(nodeId: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    this._hideDescendantsRecursive(node);
    this.notify();
  }

  private _hideDescendantsRecursive(node: DisplayNode): void {
    for (const childId of (node.data.children || [])) {
      const child = this.state.nodePool.get(childId);
      if (child) {
        this.state.hiddenNodeIds.add(child.id);
        this._hideDescendantsRecursive(child);
      }
    }
  }

  updateNodeChildren(nodeId: string, children: string[]): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    const oldChildren = node.data.children || [];
    const changed = oldChildren.length !== children.length
      || oldChildren.some((id, i) => id !== children[i]);
    node.data.children = children;
    if (changed) {
      this.trackGraphIfEditing(node.recipeId);
      // Only NEWLY added children — a pure reorder of the same set
      // (e.g. a sibling drag, which also goes through this method)
      // never needs re-validating; every id in it already satisfied
      // the invariant (or was already-broken before this call, in
      // which case it's not this change's doing to fix).
      const oldSet = new Set(oldChildren);
      for (const childId of children) {
        if (!oldSet.has(childId)) this.ensureChildBeforeParent(nodeId, childId);
      }
    }
    this.notify();
  }

  addChildToNode(parentId: string, childId: string): void {
    const node = this.state.nodePool.get(parentId);
    if (!node) return;
    node.data.children = node.data.children || [];
    if (!node.data.children.includes(childId)) {
      node.data.children.push(childId);
      this.trackGraphIfEditing(node.recipeId);
      this.ensureChildBeforeParent(parentId, childId);
    }
    this.notify();
  }

  /**
   * A newly-created child link can leave `childId` stuck at a stepOrder
   * that no longer makes sense — e.g. an EXISTING node (created, and
   * positioned, long before this link existed) just got attached as a
   * dependency of `parentId`, but nothing about creating that link
   * re-validates stepOrder the way an explicit drag in the Elaboració/
   * Timeline UI does (moveNodeToStepOrder()'s own cascade). Left alone,
   * the preparation list can show a step before the very ingredients
   * it depends on, with no indication anything is wrong. Silently
   * repairs that by pulling `childId` to sit immediately before
   * `parentId`, via the SAME cascade an explicit drag would run —
   * which also recursively fixes `childId`'s own children, if its new
   * position would in turn violate THEM. A no-op when the child
   * already sorts correctly, which is the common case (a brand-new
   * child created after its parent already needs no fix).
   */
  private ensureChildBeforeParent(parentId: string, childId: string): void {
    if (parentId === childId) return;
    const parent = this.state.nodePool.get(parentId);
    const child = this.state.nodePool.get(childId);
    if (!parent || !child || parent.recipeId !== child.recipeId) return;
    if (child.data.stepOrder < parent.data.stepOrder) return;
    const targetIndex = this.resolveStepOrderIndexRelativeTo(childId, parentId, true);
    this.moveNodeToStepOrder(childId, targetIndex);
  }

  removeChildFromNode(parentId: string, childId: string): void {
    const node = this.state.nodePool.get(parentId);
    if (!node || !node.data.children) return;
    if (node.data.children.includes(childId)) {
      node.data.children = node.data.children.filter(cid => cid !== childId);
      this.trackGraphIfEditing(node.recipeId);
    }
    this.notify();
  }

  /**
   * All current parent IDs of a node — every node in the pool whose
   * children array lists it. A node with none is a root by definition.
   */
  getParentIds(nodeId: string): string[] {
    const parents: string[] = [];
    for (const [id, node] of this.state.nodePool) {
      if ((node.data.children || []).includes(nodeId)) {
        parents.push(id);
      }
    }
    return parents;
  }

  /**
   * Move a node to `targetIndex` in its recipe's flat preparation-step
   * order, cascading whatever other nodes a dependency constraint forces
   * to move along with it (see stepOrder.ts's reorderWithCascade() for
   * the algorithm) — the drag-to-reorder entry point both the Elaboració
   * panel and Timeline UIs call into. `targetIndex` is an index into the
   * recipe's nodes sorted by their CURRENT stepOrder (0 = the very
   * start); out-of-range values are clamped, not rejected.
   */
  moveNodeToStepOrder(nodeId: string, targetIndex: number): void {
    const built = this.buildStepOrderGraph(nodeId);
    if (!built) return;
    const { graph, nodesById } = built;

    const newOrder = reorderWithCascade(graph, nodeId, targetIndex);
    for (const [id, stepOrder] of newOrder) {
      const n = nodesById.get(id);
      if (n && n.data.stepOrder !== stepOrder) {
        n.data.stepOrder = stepOrder;
        this.trackNodeIfEditing(n.recipeId, n.id);
      }
    }
    this.notify();
  }

  /**
   * Read-only counterpart of moveNodeToStepOrder() — same cascade
   * computation, but returns the resulting node-id order (sorted by the
   * new stepOrder) instead of writing anything. Used for live drag
   * previews (PreparationDragController) that need to know what WOULD
   * happen on drop, on every pointer move, without actually committing
   * anything until the drop itself.
   */
  previewStepOrder(nodeId: string, targetIndex: number): string[] {
    const built = this.buildStepOrderGraph(nodeId);
    if (!built) return [];
    const newOrder = reorderWithCascade(built.graph, nodeId, targetIndex);
    return Array.from(newOrder.entries()).sort((a, b) => a[1] - b[1]).map(([id]) => id);
  }

  /**
   * Like previewStepOrder(), but also reports which OTHER node ids the
   * dependency cascade actually forced to move alongside `nodeId` — as
   * opposed to ones that merely shifted by a slot because the moved
   * node passed by them. Used by the Elaboració panel's drag UI to
   * visually "pick up" a cascaded dependency together with the dragged
   * node, rather than leaving it to just reflow in place.
   *
   * Detected by diffing against the same insertion with NO cascade
   * repair applied (reorderWithCascade's own splice-then-fix-up, minus
   * the fix-up): a node that sits on the opposite side of `nodeId`
   * (before vs. after) in the cascaded result than it did in that naive
   * insertion was specifically pulled across — exactly what cascading
   * means. A node that's merely shifted by the insertion itself (never
   * touched by the cascade repair) keeps the same side either way, so
   * this never flags it. Deliberately a diff against stepOrder.ts's
   * pure function rather than instrumenting it to report this itself —
   * keeps that already-verified algorithm untouched.
   */
  previewStepOrderCascaded(nodeId: string, targetIndex: number): { order: string[]; cascadedIds: string[] } {
    const built = this.buildStepOrderGraph(nodeId);
    if (!built) return { order: [], cascadedIds: [] };
    const { graph } = built;

    const finalIndex = reorderWithCascade(graph, nodeId, targetIndex);
    const order = Array.from(finalIndex.entries()).sort((a, b) => a[1] - b[1]).map(([id]) => id);

    const n = graph.orderedIds.length;
    const originalIndex = graph.orderedIds.indexOf(nodeId);
    const clampedTarget = Math.max(0, Math.min(targetIndex, n - 1));
    const naiveIds = graph.orderedIds.slice();
    naiveIds.splice(originalIndex, 1);
    naiveIds.splice(Math.max(0, Math.min(clampedTarget, naiveIds.length)), 0, nodeId);
    const naiveIndex = new Map(naiveIds.map((id, i) => [id, i]));

    const movedFinalIndex = finalIndex.get(nodeId)!;
    const movedNaiveIndex = naiveIndex.get(nodeId)!;
    const cascadedIds = order.filter(id => {
      if (id === nodeId) return false;
      const wasAfter = (naiveIndex.get(id) ?? -1) > movedNaiveIndex;
      const isAfter = finalIndex.get(id)! > movedFinalIndex;
      return wasAfter !== isAfter;
    });

    return { order, cascadedIds };
  }

  /**
   * Translates "insert `nodeId` immediately before/after `anchorNodeId`"
   * into the numeric index moveNodeToStepOrder()/previewStepOrder()
   * expect. Exists because a recipe's FULL node list (every node
   * sharing nodeId's recipeId — ingredients and tools included) is
   * usually longer than whatever subset a given view actually renders
   * as draggable rows (e.g. the Elaboració panel only renders
   * prep-step-eligible nodes): a caller that only sees that rendered
   * subset can't safely compute "the Nth position overall" itself — the
   * same visible row can sit at very different numeric indices
   * depending on how many unrendered nodes (ingredients, …) happen to
   * sort before it — but it CAN always name a concrete rendered
   * sibling to anchor against, which this resolves correctly regardless
   * of anything unrendered. `anchorNodeId: null` (no sibling resolved,
   * e.g. nodeId is the only node in its recipe) falls back to 0.
   */
  resolveStepOrderIndexRelativeTo(nodeId: string, anchorNodeId: string | null, insertBefore: boolean): number {
    const built = this.buildStepOrderGraph(nodeId);
    if (!built || anchorNodeId === null) return 0;
    const withoutMoved = built.graph.orderedIds.filter(id => id !== nodeId);
    const anchorIndex = withoutMoved.indexOf(anchorNodeId);
    if (anchorIndex === -1) return withoutMoved.length;
    return insertBefore ? anchorIndex : anchorIndex + 1;
  }

  /** Shared graph-building for moveNodeToStepOrder()/previewStepOrder() — see stepOrder.ts. */
  private buildStepOrderGraph(nodeId: string): { graph: StepOrderGraph; nodesById: Map<string, DisplayNode> } | null {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return null;

    const recipeNodes = Array.from(this.state.nodePool.values())
      .filter(n => n.recipeId === node.recipeId)
      .sort((a, b) => a.data.stepOrder - b.data.stepOrder);
    const orderedIds = recipeNodes.map(n => n.id);
    const nodesById = new Map(recipeNodes.map(n => [n.id, n]));

    const graph: StepOrderGraph = {
      orderedIds,
      childrenOf: (id) => nodesById.get(id)?.data.children ?? [],
      parentsOf: (id) => this.getParentIds(id).filter(pid => nodesById.has(pid)),
    };

    return { graph, nodesById };
  }

  /**
   * Whether `descendantId` is reachable from `ancestorId` via children
   * links — used to reject a drag-and-drop reparent that would create a
   * cycle (dropping a node onto one of its own descendants).
   */
  isDescendantOf(descendantId: string, ancestorId: string): boolean {
    const visited = new Set<string>();
    const queue: string[] = [ancestorId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const current = this.state.nodePool.get(currentId);
      if (!current) continue;
      for (const childId of (current.data.children || [])) {
        if (childId === descendantId) return true;
        queue.push(childId);
      }
    }
    return false;
  }

  /**
   * Get the main root DisplayNode for a recipe (or the selected recipe,
   * when recipeId is omitted/null). Promoted from GraphVisualizer's own
   * (formerly private) copy of this exact lookup — pure derived state, so
   * any view can call it directly, same reasoning as
   * getIngredientAndPrepNodes() just below (which uses it internally).
   */
  getRecipeRootNode(recipeId?: string | null): DisplayNode | null {
    const id = recipeId ?? this.state.selectedRecipeId;
    if (!id) return null;
    const entry = this.state.loadedRecipes.get(id);
    if (!entry) return null;
    return this.state.nodePool.get(entry.rootNodeIds[entry.mainRootIndex]) ?? null;
  }

  /**
   * Traverse a recipe's graph from its main root using fold-aware rules,
   * splitting nodes into "ingredients" (leaves, or folded nodes) and
   * "preparation steps" (unfolded nodes with children) — used by the
   * Ingredients/Preparation views:
   * - Root is always treated as unfolded (always a preparation step if it
   *   has children)
   * - Unfolded nodes with children → preparation step, traverse children
   * - Folded nodes, or nodes without children → ingredient (stop there)
   * - Nodes whose ANY parent is folded are skipped entirely — but their
   *   own children are still traversed, so ingredients nested inside a
   *   folded node are still discovered.
   * - Nodes marked `hidden` are excluded from both buckets the same way
   *   (children still traversed) — a persisted per-node flag, independent
   *   of fold state, for content the user doesn't want in the elaboration
   *   at all.
   *
   * Pure derived state — no side effects — so any view can call it
   * directly without going through GraphVisualizer.
   */
  getIngredientAndPrepNodes(recipeId: string): { ingredientNodes: DisplayNode[]; prepNodes: DisplayNode[] } {
    const rootNode = this.getRecipeRootNode(recipeId);
    if (!rootNode) return { ingredientNodes: [], prepNodes: [] };

    // Build parent map for all nodes of this recipe
    const parentMap = new Map<string, DisplayNode[]>();
    for (const node of this.state.nodePool.values()) {
      if (node.recipeId !== rootNode.recipeId) continue;
      for (const childId of (node.data.children || [])) {
        if (!parentMap.has(childId)) parentMap.set(childId, []);
        parentMap.get(childId)!.push(node);
      }
    }

    const ingredientNodes: DisplayNode[] = [];
    const prepNodes: DisplayNode[] = [];
    const visited = new Set<string>();
    const queue: DisplayNode[] = [rootNode];

    while (queue.length > 0) {
      const node = queue.shift()!;
      if (visited.has(node.id)) continue;
      visited.add(node.id);

      const children = (node.data.children || [])
        .map(cid => this.state.nodePool.get(cid))
        .filter((cn): cn is DisplayNode => cn !== undefined);

      // Check if any parent is folded → skip entirely (root has no parents so this is always false for it)
      const parents = parentMap.get(node.id) || [];
      const hasFoldedParent = parents.some(p => !this.isNodeExpanded(p.id) && p.id !== rootNode.id);
      if (hasFoldedParent) continue;

      if (node.data.hidden) {
        // Hidden nodes don't appear in the elaboration at all (neither as
        // an ingredient/tool row nor as a preparation step) — but their
        // children are still traversed below, so nested content isn't lost.
      } else if (node.id === rootNode.id && children.length > 0) {
        // Root node with children → always a preparation step (shows as the final step)
        prepNodes.push(node);
      } else if (children.length > 0 && this.isNodeExpanded(node.id)) {
        // Unfolded with children → preparation step
        prepNodes.push(node);
      } else {
        // Folded or no children → ingredient
        ingredientNodes.push(node);
      }

      // Always traverse into children (even if folded), so ingredients
      // nested inside a folded node are still discovered.
      for (const child of children) {
        if (!visited.has(child.id)) {
          queue.push(child);
        }
      }
    }

    return { ingredientNodes, prepNodes };
  }

  /**
   * Make a node visible by unfolding all folded ancestors — pure store
   * operation (no viewport/camera side effects; GraphVisualizer's own
   * ensureNodeVisible() wraps this and adds those for the graph canvas).
   *
   * BFS up through parents to find the nearest folded ancestor. If that
   * ancestor is itself hidden, recursively ensure *it* is visible first,
   * then unfold it.
   */
  async revealNode(nodeId: string): Promise<void> {
    const node = this.state.nodePool.get(nodeId);
    if (!node || !this.isNodeHidden(node.id)) return; // Already visible or doesn't exist

    const parentMap = this._buildParentMap();

    // BFS up through parents to find the nearest folded ancestor
    const queue: string[] = [nodeId];
    const visited = new Set<string>([nodeId]);

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const parents = parentMap.get(currentId) || [];

      for (const parentId of parents) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = this.state.nodePool.get(parentId);
        if (!parent) continue;

        if (!this.isNodeExpanded(parent.id)) {
          // Found a folded ancestor.
          // If it's hidden, make it visible first (recursion).
          if (this.isNodeHidden(parent.id)) {
            await this.revealNode(parentId);
          }
          // Now unfold it.
          await this.expandNode(parentId);
          return;
        }

        // Not folded — continue searching up.
        queue.push(parentId);
      }
    }
  }

  updateNodeData(nodeId: string, dataUpdates: Record<string, unknown>): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    node.data = { ...node.data, ...dataUpdates } as typeof node.data;
    this.notify();
  }

  updateNodeName(nodeId: string, name: string): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    node.name = name;
    this.notify();
  }

  updateNodeType(nodeId: string, type: DisplayNode['type']): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    node.type = type;
    this.notify();
  }

  /**
   * Commit a node's position at the end of a drag gesture, so it's
   * trackable and persisted (as an offset from the main root — see
   * buildSavePayload()). Positions are otherwise mutated directly on the
   * DisplayNode object every simulation tick (d3-force needs that for
   * performance) — this is only the one commit point, called once per
   * drag, not a replacement for that. Deliberately doesn't notify(): the
   * simulation already re-renders every tick, so notifying here would
   * trigger a redundant extra render across every subscribed view.
   */
  setNodePosition(nodeId: string, x: number, y: number): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    node.x = x;
    node.y = y;
    // The main root's own position is never itself persisted (every other
    // node's position is stored as an offset *from* it), so dragging it
    // isn't a savable change.
    if (!node.isMainRoot) this.trackGraphIfEditing(node.recipeId);
  }

  setNodeInactive(nodeId: string, inactive: boolean): void {
    const node = this.state.nodePool.get(nodeId);
    if (!node) return;
    node.__inactive = inactive;
    this.notify();
  }

  setNodesInactive(nodeIds: string[], inactive: boolean): void {
    let changed = false;
    for (const nodeId of nodeIds) {
      const node = this.state.nodePool.get(nodeId);
      if (node && node.__inactive !== inactive) {
        node.__inactive = inactive;
        changed = true;
      }
    }
    if (changed) this.notify();
  }

  setAllNodesInactive(predicate: (node: DisplayNode) => boolean, inactive: boolean): void {
    let changed = false;
    for (const node of this.state.nodePool.values()) {
      if (predicate(node) && node.__inactive !== inactive) {
        node.__inactive = inactive;
        changed = true;
      }
    }
    if (changed) this.notify();
  }

  /**
   * Set every node's __inactive from a single predicate in one pass — one
   * notify() for the whole pool, not one per node. Use this instead of
   * looping `nodePool.forEach(n => store.setNodeInactive(n.id, ...))`:
   * setNodeInactive() notifies unconditionally on every call, so that loop
   * fires a full re-render (GraphVisualizer subscribes to every store
   * change) once per node in the pool — negligible for a handful of nodes,
   * but with everything background-loaded across every recipe sitting in
   * the same pool, entering edit mode or cancelling a link flow could fire
   * hundreds of synchronous re-renders in a row and visibly freeze the tab.
   */
  setNodesInactiveWhere(predicate: (node: DisplayNode) => boolean): void {
    let changed = false;
    for (const node of this.state.nodePool.values()) {
      const inactive = predicate(node);
      if (node.__inactive !== inactive) {
        node.__inactive = inactive;
        changed = true;
      }
    }
    if (changed) this.notify();
  }

  clearAllInactive(): void {
    let changed = false;
    for (const node of this.state.nodePool.values()) {
      if (node.__inactive) {
        node.__inactive = false;
        changed = true;
      }
    }
    if (changed) this.notify();
  }

  // ============================================================================
  // Subscription
  // ============================================================================

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private notify(): void {
    this.subscribers.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in store subscriber:', error);
      }
    });
  }
}
